import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import {
  Edit,
  Translate,
  CloudDownload,
  AutoFixHighRounded,
  MoreVert,
  Transform,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
  Box,
  Skeleton,
  SelectChangeEvent,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  GetSubmissionResponsePortal,
  PostSubmissionSupplementalStatusRequest,
  SubmissionAttachment,
  SubmissionPortal,
  SubmissionVersionTime,
  postSubmissionSupplementalStatus,
  getSubmissionSynopsisPortal,
  getOrgConfigPublic,
  patchSubmissionPortal,
  SurveyConfiguration,
} from "@rubi/api-generated";
import { colors } from "@rubi/ui";
import { toast } from "@rubi/ui";
import { buildAttachmentIdMap } from "@rubi/utils";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { Model, FunctionFactory } from "survey-core";

import "survey-core/survey-core.min.css";
import ExpandedMediaDisplay from "@/common/ExpandedMediaDisplay";
import { getServiceConfig } from "@/common/serviceConfigurations";
import { formatDateShort } from "@/common/utils";
import { modalDetails } from "@/components/online-reporting/constants";
import { OnlineReportingContext } from "@/components/online-reporting/context";
import { createSurveyHandlers } from "@/components/online-reporting/survey-handlers";
import {
  createCondensedModel,
  ReportAttachment,
} from "@/components/online-reporting/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import { useImageMatch } from "@/contexts/ImageMatchContext";
import { APP_OR_SERVER_URL } from "@/environment";

import CondensedReportSurvey from "./CondensedReportSurvey";
import styles from "./CondensedReportView.module.scss";
import { SaveMatchesBeforeEditModal } from "./modals/SaveMatchesBeforeEditModal";
import {
  captureScrollAnchor,
  holdScrollAnchor,
  type ScrollAnchor,
} from "./scrollAnchor";
import SectionHeader from "./SectionHeader";
import TranslateDialog from "./TranslateDialog";
import {
  applyTranslation,
  canRequestTranslation,
  hasForeignLocale,
} from "./translation";
import TranslationToggle from "./TranslationToggle";
import { getReportTimeDisplay, isReportClosed } from "./utils";
import type { SupplementalNotesDraft } from "./utils";

/**
 *
 * @param supplementaryPDFStructure - this is basically an array of the pdfs of all the supplementary reports needed for printing
 * @returns
 */
const RenderCondensedReport = ({
  reportMetadata,
  setSelectedIndex,
  selectedIndex,
  setIsSlideShowActive,
  isSlideShowActive,
  parentReportStatus,
  onReportUpdated,
  onEditStateChange,
  onBeforeEdit,
  isNotesSidePanelOpen = false,
  supplementalDraft,
  onSupplementalDraftChange,
  onSupplementalNotesSaved,
  canUpdateReports = true,
  onConvertToSupplementary,
}: {
  reportMetadata: GetSubmissionResponsePortal | SubmissionPortal;
  setSelectedIndex: Dispatch<SetStateAction<number>>;
  selectedIndex: number;
  setIsSlideShowActive: Dispatch<SetStateAction<boolean>>;
  isSlideShowActive: boolean;
  parentReportStatus: string;
  onReportUpdated?: () => Promise<void>;
  onEditStateChange?: (state: {
    isEditMode: boolean;
    handleSave: () => Promise<void>;
    isSaving: boolean;
  }) => void;
  onBeforeEdit?: () => Promise<boolean>;
  isNotesSidePanelOpen?: boolean;
  canUpdateReports?: boolean;
  supplementalDraft?: SupplementalNotesDraft;
  onSupplementalDraftChange?: (
    changes: Partial<SupplementalNotesDraft> | null,
  ) => void;
  onSupplementalNotesSaved?: (
    submissionId: string,
    notes: string,
    synopsis: string,
  ) => void;
  onConvertToSupplementary?: () => void;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("reportsv2/condensed_report");
  const isClosed = isReportClosed(reportMetadata);
  const isExported = !!reportMetadata?.last_export_time;
  const { isDevEnv } = useDevEnvironment();
  const appName = window.location.pathname.split("/")[1];
  const { id, orgId } = useParams();
  const { getSubmissionForID, getSurveyForID } = useContext(
    OnlineReportingContext,
  );
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [originalReportResponse, setOriginalReportResponse] = useState<{
    [key: string]: any;
  }>({});

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCancelEditDialogOpen, setIsCancelEditDialogOpen] = useState(false);
  const [isSaveMatchesModalOpen, setIsSaveMatchesModalOpen] = useState(false);
  const { isMatchInProgress } = useImageMatch();
  const [isTranslateDialogOpen, setIsTranslateDialogOpen] = useState(false);
  const [reportLatestVersion, setReportLatestVersion] = useState(
    reportMetadata?.latest_version?.toString() ?? "",
  );

  const [basePDFStructure, setBasePDFStructure] = useState<{
    [key: string]: any;
  }>({});
  const [attachments, setAttachments] = useState<Array<SubmissionAttachment>>(
    [],
  );
  const [newSurvey, setNewSurvey] = useState<Model>(() => {
    const newSurvey = new Model();
    // Force start in display mode to fix Edit bug
    newSurvey.mode = "display";
    return newSurvey;
  });

  const [reportAttachments, setReportAttachments] = useState<
    Array<ReportAttachment>
  >([]);
  const [baseSurveyStructure, setBaseSurveyStructure] = useState<any>({});
  const [reportVersionHistory, setReportVersionHistory] = useState<
    SubmissionVersionTime[]
  >([]);
  const [showEnglishVersion, setShowEnglishVersion] = useState(true);
  const [attachmentIDs, setAttachmentIDs] = useState<{ [key: string]: string }>(
    {},
  );
  const [versionSpecificMetadata, setVersionSpecificMetadata] = useState<{
    translation_en: any;
    locale: string | null;
  } | null>(null);
  const { currentUser } = useContext(AuthStateContext);

  const supplementalNotes =
    supplementalDraft?.notes ?? reportMetadata?.notes ?? "";
  const supplementalSynopsis =
    supplementalDraft?.synopsis ?? reportMetadata?.synopsis ?? "";
  const supplementalModified = !!supplementalDraft;
  const [
    isGeneratingSupplementalSynopsis,
    setIsGeneratingSupplementalSynopsis,
  ] = useState(false);
  const [isSavingSupplemental, setIsSavingSupplemental] = useState(false);

  const [availableSurveys, setAvailableSurveys] = useState<
    SurveyConfiguration[]
  >([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState<string>("");
  const [selectedParentSurveyId, setSelectedParentSurveyId] = useState<string>(
    reportMetadata.parent_survey_id,
  );
  const [isSurveySwitchDialogOpen, setIsSurveySwitchDialogOpen] =
    useState(false);
  const [isSwitchingSurvey, setIsSwitchingSurvey] = useState(false);
  const justSavedRef = useRef(false);
  const handleSaveRef = useRef<() => Promise<void>>();
  const rootRef = useRef<HTMLDivElement>(null);
  const pendingScrollAnchorRef = useRef<ScrollAnchor | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const organizationConfig = getServiceConfig(
    orgId ?? currentUser?.rubi_org_id ?? "NA",
    isDevEnv,
  );

  const handlers = createSurveyHandlers({
    id: reportMetadata.is_supplemental
      ? reportMetadata.submission_id
      : (id ?? reportMetadata.submission_id),
    appName,
    attachmentIDs,
    setAttachmentIDs,
    organizationConfig,
    navigate,
  });

  useEffect(() => {
    newSurvey?.onComplete.clear();
    newSurvey?.onComplete.add((sender, options) =>
      handlers.submitForm(sender, options),
    );
    newSurvey?.onUploadFiles.clear();
    newSurvey?.onUploadFiles.add((survey, options) =>
      handlers.uploadFiles(survey, options),
    );
    newSurvey?.onClearFiles.clear();
    newSurvey?.onClearFiles.add((_, options) => handlers.clearFiles(options));

    // Add event handlers to update owner choices when values change
    newSurvey?.onValueChanged.clear();
    newSurvey?.onValueChanged.add((_, options) => {
      // Update owner choices when relevant fields change
      if (
        options.name === "persons-involved" ||
        options.name === "person-reportingParty_firstName" ||
        options.name === "person-reportingParty_lastName" ||
        options.name === "person-reportingParty_dob" ||
        options.name === "items-stolen"
      ) {
        updateOwnerChoices(newSurvey);
      }
    });

    // Update owner choices when page changes to ensure dropdowns are populated
    newSurvey?.onCurrentPageChanged.clear();
    newSurvey?.onCurrentPageChanged.add(() => {
      updateOwnerChoices(newSurvey);
    });

    // Initial population of owner choices
    if (newSurvey) {
      updateOwnerChoices(newSurvey);
    }
  }, [newSurvey, handlers]);

  useEffect(() => {
    if (onEditStateChange) {
      onEditStateChange({
        isEditMode: newSurvey.mode === "edit",
        handleSave: async () => {
          if (handleSaveRef.current) {
            await handleSaveRef.current();
          }
        },
        isSaving,
      });
    }
  }, [newSurvey.mode, isSaving, onEditStateChange]);

  // Swapping in the edit model re-renders every field; keep the reviewer's place.
  useLayoutEffect(() => {
    const anchor = pendingScrollAnchorRef.current;
    if (!anchor || !rootRef.current) return;
    pendingScrollAnchorRef.current = null;
    return holdScrollAnchor(rootRef.current, anchor);
  }, [newSurvey]);

  const getSurveyResult = async () => {
    // Use the selected version (reportLatestVersion) for normal version switching
    // This allows users to view different versions without being forced to latest
    const currentVersion = reportVersionHistory.find(
      (version) => version.version === parseInt(reportLatestVersion),
    );
    const surveyIdToUse = currentVersion?.survey_id || reportMetadata.survey_id;

    const result = await getSurveyForID(surveyIdToUse);
    if (result) {
      return result;
    } else {
      throw new Error("Survey result is null");
    }
  };

  const fetchAvailableSurveys = async () => {
    try {
      const response = await getOrgConfigPublic({
        path: { org_id: orgId ?? "" },
      });
      const surveys = response.data?.surveys || [];
      const surveyIdToUse =
        reportVersionHistory.find(
          (version) => version.version === parseInt(reportLatestVersion),
        )?.survey_id || reportMetadata.survey_id;

      // Filter to show only non-registry, non-supplemental surveys
      const availableSurveys = surveys
        .filter(
          (survey: SurveyConfiguration) =>
            !survey.is_registry && !survey.is_supplemental,
        )
        .sort((a: SurveyConfiguration, b: SurveyConfiguration) => {
          return a.name.localeCompare(b.name);
        });

      setAvailableSurveys(availableSurveys);

      if (availableSurveys.find((survey) => survey.id === surveyIdToUse)) {
        setSelectedSurveyId(surveyIdToUse);
      } else {
        // If the survey has a new version, the IDs won't line up
        // Grab the latest with the same class
        const matchingCurrentVersion = availableSurveys.find(
          (survey) => survey.survey_class === reportMetadata.survey_class,
        );
        if (matchingCurrentVersion) {
          setSelectedSurveyId(matchingCurrentVersion.survey_id);
        } else {
          setSelectedSurveyId(availableSurveys[0].survey_id);
        }
      }
      setSelectedParentSurveyId(reportMetadata.parent_survey_id);
    } catch (error) {
      console.error("Failed to fetch available surveys:", error);
    }
  };

  const handleSurveyTypeChange = (event: SelectChangeEvent<string>) => {
    setSelectedSurveyId(event.target.value);
    setIsSurveySwitchDialogOpen(true);
  };

  const handleSurveySwitchConfirm = async () => {
    if (!selectedSurveyId) return;

    setIsSwitchingSurvey(true);
    try {
      const updatedSubmission = await patchSubmissionPortal({
        path: { submission_id: reportMetadata.submission_id },
        body: { new_survey_id: selectedSurveyId },
      });
      if (updatedSubmission.data?.survey_id) {
        setSelectedSurveyId(updatedSubmission.data?.survey_id);
      }

      toast.success("Report type switched successfully!");

      // Refresh report data without page reload
      if (onReportUpdated) {
        justSavedRef.current = true;
        await onReportUpdated();
      } else {
        // Fallback to page reload if callback not provided
        window.location.reload();
      }
    } catch (error) {
      console.error("Failed to switch report type:", error);
      toast.error("Failed to switch report type. Please try again.");
    } finally {
      setIsSwitchingSurvey(false);
      setIsSurveySwitchDialogOpen(false);
      setSelectedSurveyId("");
    }
  };

  const getMediaIndexUrl = (attachments: any[], index: number) => {
    const file = reportAttachments[index].incident_evidence_upload[0];
    const isImage = file.type?.includes("image");
    const isVideo = file.type?.includes("video");
    const isPdf = file.type?.includes("application/pdf");
    if (!isImage && !isVideo && !isPdf && file.pdf_url) {
      return file.pdf_url;
    }
    return file.content;
  };

  const handleSave = async () => {
    const hasErrors = newSurvey.hasErrors(true, false);
    if (hasErrors) {
      toast.error("Please resolve all errors before saving.");
      throw new Error("Validation errors present");
    }
    // newSurvey.showCompletePage = false;
    newSurvey.mode = "display";
    setNewSurvey(newSurvey);

    // newSurvey.doComplete();
    setIsSaving(true);
    try {
      await handlers.submitForm(newSurvey, {
        showSaveInProgress: () => {},
        showSaveSuccess: () => {
          toast.success("Report saved successfully");
        },
        showSaveError: (error: any) => {
          console.error("Error saving changes:", error);
          toast.error("Failed to save changes. Please try again.");
        },
        currentStatus: reportMetadata.status,
      });

      // Refresh report data without page reload
      if (onReportUpdated) {
        justSavedRef.current = true;
        await onReportUpdated();
      } else {
        // Fallback to page reload if callback not provided
        window.location.reload();
      }
    } catch (error) {
      console.error("Error saving report:", error);
      toast.error("Failed to save report. Please try again.");
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // Store handleSave in ref for parent access
  handleSaveRef.current = handleSave;

  const handleCancelEdit = async () => {
    const { newSurvey: displaySurvey } = await createCondensedModel(
      basePDFStructure,
      originalReportResponse,
      attachments,
      true,
      false,
      false,
      reportMetadata.submission_id,
      reportMetadata.survey_class,
      reportMetadata.time_submitted ?? reportMetadata.time_created,
    );
    displaySurvey.mode = "display";
    setNewSurvey(displaySurvey);
    setIsCancelEditDialogOpen(false);
  };

  const EditButton = () => {
    const isEditMode = newSurvey.mode === "edit";

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip
          title={
            isExported
              ? "Exported records cannot be modified"
              : isEditMode
                ? "Save changes"
                : modalDetails.edit.description
          }
          arrow
        >
          <span>
            <LoadingButton
              variant="contained"
              color={isEditMode ? "success" : "info"}
              size="small"
              disabled={isExported}
              onClick={
                isEditMode
                  ? handleSave
                  : () => {
                      if (isMatchInProgress) {
                        setIsSaveMatchesModalOpen(true);
                      } else {
                        setIsEditDialogOpen(true);
                      }
                    }
              }
              endIcon={isEditMode ? null : <Edit fontSize="small" />}
              loading={isSaving}
              sx={{
                bgcolor: isEditMode ? undefined : colors.primary.main,
                color: colors.common.white,
                borderRadius: 1,
                px: 2,
                py: 0.5,
                fontSize: 13,
                fontWeight: 500,
                letterSpacing: "0.46px",
                textTransform: "uppercase",
                boxShadow:
                  "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
                "&:hover": {
                  bgcolor: isEditMode ? undefined : colors.primary.dark,
                  boxShadow:
                    "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
                },
                "& .MuiSvgIcon-root": { width: 18, height: 18 },
              }}
            >
              {isEditMode ? "Save" : "Edit"}
            </LoadingButton>
          </span>
        </Tooltip>
        {isEditMode && (
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => setIsCancelEditDialogOpen(true)}
            sx={{
              textTransform: "uppercase",
              fontWeight: 600,
              fontSize: "0.75rem",
              px: 1.5,
            }}
          >
            {t("cancel")}
          </Button>
        )}
      </Box>
    );
  };

  const downloadPDF = async () => {
    let backend = APP_OR_SERVER_URL;
    if (organizationConfig?.apiRegion === "us") {
      backend = backend.replace("prod.api", "us.api");
    }
    window.open(
      `${backend}/report/portal/submission/${reportMetadata.submission_id}/pdf?combined=false`,
      "_self",
    );
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleTranslateClick = () => {
    handleMenuClose();
    setIsTranslateDialogOpen(true);
  };

  const handleDownloadClick = () => {
    handleMenuClose();
    downloadPDF();
  };

  async function updateSurveyOne(submissionVersion: string) {
    setReportLatestVersion(submissionVersion);

    if (getSubmissionForID) {
      const submissionId = reportMetadata.is_supplemental
        ? reportMetadata.submission_id
        : (id ?? "");
      const result = await getSubmissionForID(submissionId, submissionVersion);
      const submission = result?.submission;
      if (submission) {
        setOriginalReportResponse(submission);

        // Update the version-specific metadata including translation_en
        // This ensures that when viewing different versions, the correct translation data is used
        setVersionSpecificMetadata({
          translation_en: result?.translation_en || null,
          locale: result?.locale || null,
        });
      }

      const localSurvey = new Model(baseSurveyStructure);
      localSurvey.data = submission;
    }
  }

  // Update to latest version only after a save operation
  useEffect(() => {
    if (justSavedRef.current && reportMetadata?.latest_version) {
      const newLatestVersion = reportMetadata.latest_version.toString();
      setReportLatestVersion(newLatestVersion);
      justSavedRef.current = false;
    }
  }, [reportMetadata?.latest_version]);

  useEffect(() => {
    const fetchSurveyResultAndUpdateStates = async () => {
      try {
        const result = await getSurveyResult();

        if (result) {
          setBasePDFStructure(result.survey.pdf_configuration ?? {});
          setBaseSurveyStructure(result.survey);

          setAttachmentIDs(
            buildAttachmentIdMap(
              reportMetadata.submission,
              reportMetadata.attachments ?? [],
            ),
          );

          setAttachments(reportMetadata.attachments ?? []);
          const localSurvey = new Model(result.survey.pdf_configuration ?? {});
          const submission = reportMetadata.submission;
          localSurvey.data = submission;
          if (submission) {
            setOriginalReportResponse(submission);
          }
          setReportVersionHistory(reportMetadata.version_times ?? []);

          // Fetch available surveys for switching
          await fetchAvailableSurveys();

          // Initialize version-specific metadata with the current report metadata
          setVersionSpecificMetadata({
            translation_en: reportMetadata.translation_en,
            locale: reportMetadata.locale || null,
          });
        }
      } catch (error) {
        console.error("Failed to fetch survey result:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSurveyResultAndUpdateStates();
  }, [id, reportMetadata, reportLatestVersion]);

  // Helper function to get the current locale (version-specific or fallback to main metadata)
  const getCurrentLocale = () => {
    return versionSpecificMetadata?.locale || reportMetadata?.locale;
  };

  const getTranslatedResponse = () => {
    const metadataToUse = versionSpecificMetadata || reportMetadata;
    return applyTranslation(
      originalReportResponse,
      metadataToUse?.translation_en,
    );
  };

  // Add owner choices logic for vehicle and item ownership dropdowns
  const getOwnerChoices = (survey: any) => {
    const choices: Array<{
      value: { dob: string; fullName: string; id: string };
      text: string;
    }> = [];

    // Add reporting party as the first option
    const id = survey.getValue("person-reportingParty_internalId") || "";
    const reportingPartyFirstName =
      survey.getValue("person-reportingParty_firstName") || "";
    const reportingPartyLastName =
      survey.getValue("person-reportingParty_lastName") || "";

    if (reportingPartyFirstName || reportingPartyLastName) {
      const fullName =
        `${reportingPartyFirstName} ${reportingPartyLastName}`.trim();
      const dob = survey.getValue("person-reportingParty_dob") || "";
      const displayName = fullName + " (" + dob + ")";

      if (fullName) {
        choices.push({
          text: displayName,
          value: { dob, fullName, id },
        });
      }
    }

    const business_id =
      survey.getValue("business-reportingParty_internalId") || "";
    const business_name = survey.getValue("business-reportingParty_name") || "";
    if (business_name) {
      choices.push({
        text: business_name,
        value: { dob: "", fullName: business_name, id: business_id },
      });
    }

    // Add involved persons
    const involvedPersons = survey.getValue("persons-involved");
    if (involvedPersons && Array.isArray(involvedPersons)) {
      involvedPersons.forEach(
        (person: { [key: string]: string }, index: number) => {
          if (
            person["person-involved_involvement"] === "Witness" ||
            person["person-involved_involvement"] === "Suspect"
          ) {
            return;
          }

          const id = person["person-involved_internalId"] || "";
          const firstName = person["person-involved_firstName"] || "";
          const lastName = person["person-involved_lastName"] || "";
          const dob = person["person-involved_dob"] || "";
          const name = person["person-involved_name"] || ""; // LPS only has a name field for involved person

          if (firstName || lastName) {
            const fullName = `${firstName} ${lastName}`.trim();
            const displayName =
              (dob ? fullName + " (" + dob + ")" : fullName) ||
              `Person ${index + 1}`;

            choices.push({
              text: displayName,
              value: { dob, fullName, id },
            });
          } else if (name) {
            const fullName = name.trim();
            choices.push({
              text: fullName,
              value: { dob, fullName, id },
            });
          }
        },
      );
    }

    return choices;
  };

  // Function to update vehicle and item owner choices based on involved persons
  const updateOwnerChoices = (survey: any) => {
    const questions = [
      "vehicle-intruded_ownerDetails",
      "vehicle-damaged_ownerDetails",
      "vehicle-vulnerable_ownerDetails",
    ];
    const dynamicPanels: Array<{ panelName: string; questionName: string }> = [
      { panelName: "items-stolen", questionName: "item-involved_ownerDetails" },
      {
        panelName: "items-involved",
        questionName: "item-involved_ownerDetails",
      },
      { panelName: "items-lost", questionName: "item-involved_ownerDetails" },
      {
        panelName: "items-damaged",
        questionName: "item-involved_ownerDetails",
      },
      {
        panelName: "bicycles-registered",
        questionName: "bicycles-registered_ownerDetails",
      },
      {
        panelName: "vehicle-intruded-panel",
        questionName: "vehicle-intruded_ownerDetails",
      },
      {
        panelName: "vehicles-intruded",
        questionName: "vehicle-intruded_ownerDetails",
      },
      {
        panelName: "vehicles-damaged",
        questionName: "vehicle-damaged_ownerDetails",
      },
    ];

    const choices = getOwnerChoices(survey);

    for (const question of questions) {
      const ownerQuestion = survey.getQuestionByName(question);
      if (!ownerQuestion) {
        continue;
      }

      ownerQuestion.choices = choices;
    }

    for (const { panelName, questionName } of dynamicPanels) {
      const panelQuestion = survey.getQuestionByName(panelName);
      if (!panelQuestion) {
        continue;
      }

      panelQuestion.templateElements.forEach(
        (el: {
          name: string;
          choices: Array<{
            value: { dob: string; fullName: string; id: string };
            text: string;
          }>;
        }) => {
          if (el.name === questionName) {
            el.choices = choices;
          }
        },
      );
    }
  };

  async function unlinkReport(reportSubmissionId: string) {
    try {
      const payload: PostSubmissionSupplementalStatusRequest = {
        is_supplemental: true,
        original_id: null,
      };
      postSubmissionSupplementalStatus({
        path: { submission_id: reportSubmissionId },
        body: payload,
      }).then(async () => {
        toast.success("Report unlinked successfully");
        if (onReportUpdated) {
          await onReportUpdated();
        }
        navigate(
          `/${appName}/${orgId}/report/${reportSubmissionId}/condensed`,
          { replace: true },
        );
      });
    } catch (error) {
      console.error("Error while unlinking report ", error);
    }
  }

  const generateSupplementalSynopsis = async () => {
    setIsGeneratingSupplementalSynopsis(true);
    try {
      const response = (
        await getSubmissionSynopsisPortal({
          path: { submission_id: reportMetadata.submission_id },
        })
      ).data!;

      onSupplementalDraftChange?.({ synopsis: response.synopsis });
    } catch (error) {
      console.error("Error generating synopsis:", error);
    } finally {
      setIsGeneratingSupplementalSynopsis(false);
    }
  };

  const updateSupplementalNotes = async () => {
    setIsSavingSupplemental(true);
    try {
      await patchSubmissionPortal({
        path: { submission_id: reportMetadata.submission_id },
        body: {
          notes: supplementalNotes,
          synopsis: supplementalSynopsis,
        },
      });
      toast.success("Report notes updated successfully");
      onSupplementalNotesSaved?.(
        reportMetadata.submission_id,
        supplementalNotes,
        supplementalSynopsis,
      );
    } catch (error) {
      toast.error("Failed to update report notes, please try again later");
    } finally {
      setIsSavingSupplemental(false);
    }
  };

  useEffect(() => {
    if (!basePDFStructure.pages || !originalReportResponse || !attachments)
      return;

    async function getData() {
      const translatedResponse = getTranslatedResponse();

      const { newSurvey, reportAttachments } = await createCondensedModel(
        basePDFStructure,
        translatedResponse || originalReportResponse,
        attachments,
        true,
        false,
        false,
        reportMetadata.submission_id,
        reportMetadata.survey_class,
        reportMetadata.time_submitted ?? reportMetadata.time_created,
      );
      setNewSurvey(newSurvey);
      setReportAttachments(reportAttachments);
    }
    getData();
  }, [basePDFStructure, originalReportResponse, attachments, id]);

  const toggleLanguageVersion = async (showTranslated: boolean) => {
    if (showTranslated === showEnglishVersion) return;
    if (showEnglishVersion) {
      const { newSurvey: updatedSurvey } = await createCondensedModel(
        basePDFStructure,
        originalReportResponse,
        attachments,
        true,
        false,
        false,
        reportMetadata.submission_id,
        reportMetadata.survey_class,
        reportMetadata.time_submitted ?? reportMetadata.time_created,
      );
      setNewSurvey(updatedSurvey);
    } else {
      const translatedResponse = getTranslatedResponse();
      const { newSurvey: updatedSurvey } = await createCondensedModel(
        basePDFStructure,
        translatedResponse || originalReportResponse,
        attachments,
        true,
        false,
        false,
        reportMetadata.submission_id,
        reportMetadata.survey_class,
        reportMetadata.time_submitted ?? reportMetadata.time_created,
      );
      setNewSurvey(updatedSurvey);
    }
    setShowEnglishVersion(!showEnglishVersion);
  };

  if (loading) {
    return (
      <div
        style={{
          margin: "60px auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <Paper
        elevation={3}
        className={styles.paperContainer}
        style={{
          border: "1px solid var(--divider)",
          paddingTop: 0,
        }}
      >
        <div
          style={{
            position: "sticky",
            top: "var(--top-bar-height, 70px)",
            zIndex: 9,
            // Bleed to card edges: must match .paperContainer's 8px padding.
            margin: "0 -8px",
            padding: "4px 8px 0 8px",
            // Sticky: must be opaque or page content shows through as it
            // scrolls under. Same tint-over-card composite as SectionHeader.
            background:
              "linear-gradient(var(--cr-fill), var(--cr-fill)), var(--cr-card-bg)",
            borderTopLeftRadius: "inherit",
            borderTopRightRadius: "inherit",
          }}
        >
          <SectionHeader
            title={
              reportMetadata.is_supplemental
                ? t("supplemental_report")
                : t("incident_report")
            }
            subtitle={
              <>
                {t("version")}:{reportMetadata.latest_version} -{" "}
                {getReportTimeDisplay(
                  reportMetadata.time_submitted ?? reportMetadata.time_created,
                  reportMetadata.time_updated,
                )}
              </>
            }
            actions={
              <>
                {!isClosed &&
                  canUpdateReports &&
                  reportMetadata.latest_version ===
                    parseInt(reportLatestVersion) && <EditButton />}
                {reportMetadata.is_supplemental &&
                  reportMetadata.is_linked &&
                  !["approved", "rejected"].includes(parentReportStatus) && (
                    <Tooltip
                      title={
                        isExported ? "Exported records cannot be modified" : ""
                      }
                      arrow
                    >
                      <span>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          disabled={isExported}
                          sx={{
                            textTransform: "uppercase",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            px: 1.5,
                            py: 0.5,
                          }}
                          onClick={() =>
                            unlinkReport(reportMetadata.submission_id)
                          }
                        >
                          Unlink
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                <IconButton
                  onClick={handleMenuOpen}
                  size="small"
                  aria-label="More options"
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              </>
            }
          />

          {/* Menu for Translate and Download */}
          <Menu
            anchorEl={menuAnchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            {canRequestTranslation(getCurrentLocale()) && (
              <MenuItem onClick={handleTranslateClick}>
                <ListItemIcon>
                  <Translate fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t("translate")}</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleDownloadClick}>
              <ListItemIcon>
                <CloudDownload fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download PDF</ListItemText>
            </MenuItem>
            {!reportMetadata.is_supplemental && onConvertToSupplementary && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onConvertToSupplementary();
                }}
              >
                <ListItemIcon>
                  <Transform fontSize="small" />
                </ListItemIcon>
                <ListItemText>Convert to Supplementary</ListItemText>
              </MenuItem>
            )}
          </Menu>
          {reportMetadata.is_supplemental && reportMetadata.is_linked && (
            <Box mt={1} mb={2} pl={2}>
              {reportMetadata.last_updated_by?.first_name ? (
                <Typography variant="body2" color="textSecondary">
                  <strong>Linked by: </strong>
                  {`${reportMetadata.last_updated_by?.first_name} ${reportMetadata.last_updated_by?.last_name}`}{" "}
                  on{" "}
                  {formatDateShort(
                    reportMetadata.time_submitted ??
                      reportMetadata.time_created,
                  )}
                </Typography>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  <strong>Autolinked: </strong>
                  According to the referenced report number at the time of
                  submission on{" "}
                  {formatDateShort(
                    reportMetadata.time_submitted ??
                      reportMetadata.time_created,
                  )}
                </Typography>
              )}
            </Box>
          )}

          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid
              size={{ xs: 12, sm: 6 }}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {hasForeignLocale(getCurrentLocale()) && (
                <TranslationToggle
                  locale={getCurrentLocale()!}
                  showTranslated={showEnglishVersion}
                  onChange={toggleLanguageVersion}
                />
              )}
            </Grid>
          </Grid>
        </div>

        {!isNotesSidePanelOpen &&
          reportMetadata.is_supplemental &&
          reportMetadata.is_linked && (
            <Paper
              elevation={0}
              className={styles.paperContainer}
              sx={{ mt: 2, boxShadow: "none !important" }}
              style={{ paddingBottom: "12px" }}
            >
              <Typography variant="h6" sx={{ my: 2, fontWeight: 600 }}>
                Supplemental Report Notes & Synopsis
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Tooltip
                  title={
                    isExported ? "Exported records cannot be modified" : ""
                  }
                  arrow
                >
                  <FormControl fullWidth>
                    <TextField
                      id="supplemental-notes"
                      label="Internal Private Notes"
                      value={supplementalNotes}
                      disabled={isClosed || isExported}
                      variant="outlined"
                      multiline
                      minRows={3}
                      onChange={(e) =>
                        onSupplementalDraftChange?.({ notes: e.target.value })
                      }
                      sx={{
                        "& .MuiInputLabel-root": {
                          color: supplementalModified
                            ? "primary.main"
                            : "text.primary",
                        },
                        "& .MuiOutlinedInput-root": {
                          borderColor: supplementalModified
                            ? "primary.main"
                            : "divider",
                        },
                      }}
                    />
                  </FormControl>
                </Tooltip>

                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 0 }}>
                      Synopsis
                    </Typography>
                    {!isClosed && !isExported && (
                      <LoadingButton
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={generateSupplementalSynopsis}
                        loading={isGeneratingSupplementalSynopsis}
                        disabled={isClosed || isExported}
                        startIcon={<AutoFixHighRounded />}
                      >
                        {supplementalSynopsis ? "Regenerate" : "Generate"}
                      </LoadingButton>
                    )}
                  </Box>

                  {!isClosed && !isExported && (
                    <Typography
                      variant="body2"
                      sx={{ mb: 2, color: "text.secondary" }}
                    >
                      <b>
                        For the best synopsis, please correct any errors and add
                        missing details to the supplemental report first. The
                        synopsis will be included in the RMS export but
                        won&apos;t be shared with the reporting individual.
                      </b>
                    </Typography>
                  )}

                  {isGeneratingSupplementalSynopsis ? (
                    <Box
                      sx={{
                        border: "1px solid lightgrey",
                        borderRadius: "4px",
                        padding: "8px",
                        marginTop: "16px",
                      }}
                    >
                      <Skeleton variant="text" />
                      <Skeleton variant="text" />
                      <Skeleton variant="text" />
                    </Box>
                  ) : (
                    <Tooltip
                      title={
                        isExported ? "Exported records cannot be modified" : ""
                      }
                      arrow
                    >
                      <FormControl fullWidth>
                        <TextField
                          id="supplemental-synopsis"
                          value={supplementalSynopsis}
                          disabled={isClosed || isExported}
                          variant="outlined"
                          multiline
                          minRows={3}
                          placeholder="No synopsis generated yet."
                          helperText={
                            supplementalSynopsis
                              ? "Generated synopsis may contain errors. Please review and update important information."
                              : ""
                          }
                          onChange={(e) =>
                            onSupplementalDraftChange?.({
                              synopsis: e.target.value,
                            })
                          }
                        />
                      </FormControl>
                    </Tooltip>
                  )}
                </Box>

                {!isClosed && !isExported && (
                  <LoadingButton
                    variant="contained"
                    color="info"
                    size="medium"
                    loading={isSavingSupplemental}
                    onClick={updateSupplementalNotes}
                    disabled={!supplementalModified}
                    sx={{
                      mt: 2,
                      py: 1,
                      fontSize: "0.9rem",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    Save Changes
                  </LoadingButton>
                )}
              </Box>
            </Paper>
          )}

        <Box
          sx={{
            display: "flex",
            gap: 2,
            px: 3,
            py: 1.5,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 240, flex: 1 }}>
            <Typography
              id="demo-simple-select-label"
              variant="caption"
              sx={{
                fontSize: 10,
                color: "text.primary",
                letterSpacing: "0.8px",
                lineHeight: "12px",
                textTransform: "uppercase",
                fontWeight: 700,
                mb: 0.5,
              }}
            >
              {t("displaying")}
            </Typography>
            <Select
              id="demo-simple-select"
              value={reportLatestVersion}
              variant="outlined"
              size="small"
              onChange={(event) => updateSurveyOne(event.target.value)}
            >
              {reportVersionHistory
                .sort((a, b) => b.version - a.version)
                .map((version) => {
                  const timeToUse =
                    version.version === 1
                      ? (reportMetadata.time_submitted ??
                        reportMetadata.time_created)
                      : version.time_created;
                  return (
                    <MenuItem
                      key={`version-${version.version}`}
                      value={version.version}
                    >
                      {version.version === reportMetadata.latest_version ? (
                        <span>
                          <strong>{t("latest_version")}</strong> -{" "}
                          {getReportTimeDisplay(timeToUse)}
                          {version.survey_id &&
                            version.survey_id !== reportMetadata.survey_id && (
                              <span
                                style={{ color: "#666", fontSize: "0.9em" }}
                              >
                                {" "}
                                (Different Report Type)
                              </span>
                            )}
                        </span>
                      ) : (
                        <span>
                          {t("version")} {version.version} -{" "}
                          {getReportTimeDisplay(timeToUse)}
                          {version.survey_id &&
                            version.survey_id !== reportMetadata.survey_id && (
                              <span
                                style={{ color: "#666", fontSize: "0.9em" }}
                              >
                                {" "}
                                (Different Report Type)
                              </span>
                            )}
                        </span>
                      )}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>

          {availableSurveys.length > 1 && !reportMetadata.is_supplemental && (
            <FormControl size="small" sx={{ minWidth: 240, flex: 1 }}>
              <Typography
                id="survey-type-select-label"
                variant="caption"
                sx={{
                  fontSize: 10,
                  color: "text.primary",
                  letterSpacing: "0.8px",
                  lineHeight: "12px",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  mb: 0.5,
                }}
              >
                Report Type
              </Typography>
              <Select
                id="survey-type-select"
                value={selectedParentSurveyId}
                variant="outlined"
                size="small"
                onChange={handleSurveyTypeChange}
                displayEmpty
                disabled={isClosed}
              >
                <MenuItem value="" disabled>
                  <em>Select a different report type</em>
                </MenuItem>
                {availableSurveys.map((survey) => (
                  <MenuItem key={survey.survey_id} value={survey.survey_id}>
                    {survey.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {(() => {
          const latestVersionNum = parseInt(reportLatestVersion);
          const currentNote = reportVersionHistory.find(
            (v) => v.version === latestVersionNum,
          )?.modification_request_note;
          const previousNote = reportVersionHistory.find(
            (v) => v.version === latestVersionNum - 1,
          )?.modification_request_note;
          return (
            <>
              {currentNote && (
                <Box sx={{ px: 3, my: 4 }}>
                  <TextField
                    id="modification-request-note-current"
                    variant="outlined"
                    label={t("modification_request_note")}
                    multiline
                    fullWidth
                    minRows={3}
                    value={currentNote}
                    disabled={true}
                  />
                </Box>
              )}
              {previousNote && (
                <Box sx={{ px: 3, my: 4 }}>
                  <TextField
                    id="modification-request-note-previous"
                    variant="outlined"
                    label={t("modification_request_note_previous")}
                    multiline
                    fullWidth
                    minRows={3}
                    value={previousNote}
                    disabled={true}
                  />
                </Box>
              )}
            </>
          );
        })()}
        <CondensedReportSurvey
          model={newSurvey}
          editing={newSurvey.mode === "edit"}
          containerClassName={styles.reportSurveyContainer}
        />
        {selectedIndex >= 0 && (
          <ExpandedMediaDisplay
            isSlideShowActive={isSlideShowActive}
            setIsSlideShowActive={setIsSlideShowActive}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            slideshowLength={reportAttachments.length}
            mediaSrc={getMediaIndexUrl(reportAttachments, selectedIndex)}
            mediaType={(() => {
              const file =
                reportAttachments[selectedIndex]?.incident_evidence_upload[0];
              if (file?.type?.includes("application/pdf")) return "pdf";
              if (file?.type?.includes("image")) return "image";
              if (file?.type?.includes("video")) return "video";
              if (file?.pdf_url) return "pdf";
              return "video";
            })()}
          />
        )}
        <Dialog
          open={isEditDialogOpen}
          onClose={() => {
            setIsEditDialogOpen(false);
          }}
        >
          <DialogTitle>{t("edit_this_report")}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t("editing_report_note")}</DialogContentText>
            <DialogContentText>
              <br />
              <b>{t("editing_report_note_confirm")}</b>
            </DialogContentText>
          </DialogContent>
          <DialogActions
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "16px",
            }}
          >
            <Button
              onClick={() => {
                setIsEditDialogOpen(false);
              }}
              color="secondary"
              variant="outlined"
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={async () => {
                const scrollAnchor = captureScrollAnchor(rootRef.current);
                if (onBeforeEdit && !(await onBeforeEdit())) {
                  setIsEditDialogOpen(false);
                  return;
                }
                // Always create a new survey with original data for editing
                // This ensures users can only edit the original report, not the translated version
                // Even if they're currently viewing the translated version, editing should always work with original data
                const { newSurvey: editSurvey } = await createCondensedModel(
                  basePDFStructure,
                  originalReportResponse, // Always use original data for editing
                  attachments,
                  true,
                  false,
                  false,
                  reportMetadata.submission_id,
                  reportMetadata.survey_class,
                  reportMetadata.time_submitted ?? reportMetadata.time_created,
                );

                editSurvey.mode = "edit";

                // Register the generateInternalId function
                FunctionFactory.Instance.register(
                  "generateInternalId",
                  (params: number[]) => {
                    const existingValue = params[0];
                    if (existingValue) {
                      return existingValue;
                    }
                    return Math.trunc(Math.random() * 99999999).toString();
                  },
                );

                // Update owner choices for edit mode
                updateOwnerChoices(editSurvey);

                pendingScrollAnchorRef.current = scrollAnchor;
                setNewSurvey(editSurvey);
                setIsEditDialogOpen(false);
              }}
              color="primary"
              variant="contained"
            >
              {t("confirm")}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={isCancelEditDialogOpen}
          onClose={() => setIsCancelEditDialogOpen(false)}
        >
          <DialogTitle>{t("cancel_edit_title")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("cancel_edit_description")}
            </DialogContentText>
          </DialogContent>
          <DialogActions
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "16px",
            }}
          >
            <Button
              onClick={() => setIsCancelEditDialogOpen(false)}
              color="secondary"
              variant="outlined"
            >
              {t("keep_editing")}
            </Button>
            <Button
              onClick={handleCancelEdit}
              color="warning"
              variant="contained"
            >
              {t("discard_changes")}
            </Button>
          </DialogActions>
        </Dialog>
        <SaveMatchesBeforeEditModal
          isOpen={isSaveMatchesModalOpen}
          onClose={() => setIsSaveMatchesModalOpen(false)}
          onProceedToEdit={() => {
            setIsSaveMatchesModalOpen(false);
            setIsEditDialogOpen(true);
          }}
        />
        <TranslateDialog
          open={isTranslateDialogOpen}
          onClose={() => setIsTranslateDialogOpen(false)}
          submissionId={reportMetadata.submission_id}
          onTranslated={async () => {
            if (onReportUpdated) {
              justSavedRef.current = true;
              await onReportUpdated();
            } else {
              window.location.reload();
            }
          }}
        />

        {/* Survey Switch Confirmation Dialog */}
        <Dialog
          open={isSurveySwitchDialogOpen}
          onClose={() => {
            setIsSurveySwitchDialogOpen(false);
            setSelectedSurveyId("");
          }}
        >
          <DialogTitle>Switch Report Type</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to switch the report type for this report?
            </DialogContentText>
            <DialogContentText sx={{ mt: 2 }}>
              <strong>Current Survey:</strong>{" "}
              {availableSurveys.find(
                (s) => s.survey_id === reportMetadata.parent_survey_id,
              )?.name || `Survey ID: ${reportMetadata.survey_id}`}
            </DialogContentText>
            <DialogContentText>
              <strong>New Survey:</strong>{" "}
              {
                availableSurveys.find((s) => s.survey_id === selectedSurveyId)
                  ?.name
              }
            </DialogContentText>
            <DialogContentText sx={{ mt: 2, color: "warning.main" }}>
              <strong>Note:</strong> This will create a new version of the
              report with the updated survey configuration. All existing data
              will be preserved in the new version.
            </DialogContentText>
          </DialogContent>
          <DialogActions
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "16px",
            }}
          >
            <Button
              onClick={() => {
                setIsSurveySwitchDialogOpen(false);
                setSelectedSurveyId("");
              }}
              color="secondary"
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSurveySwitchConfirm}
              color="primary"
              variant="contained"
              disabled={isSwitchingSurvey}
            >
              {isSwitchingSurvey ? "Switching..." : "Confirm Switch"}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </div>
  );
};

export default RenderCondensedReport;
